import { useCallback } from "react";
import { getAnalyticsInstance, logEvent, setAnalyticsUserId } from "../firebase";

// Analytics Event Names
export const AnalyticsEvents = {
  // Page/Screen Views
  PAGE_VIEW: "page_view",
  
  // Authentication
  ANONYMOUS_SIGNIN: "anonymous_signin",
  
  // QR/Room Events
  QR_SCAN_STARTED: "qr_scan_started",
  QR_CODE_DETECTED: "qr_code_detected",
  MANUAL_CODE_ENTERED: "manual_code_entered",
  ROOM_JOIN_STARTED: "room_join_started",
  ROOM_JOIN_SUCCESS: "room_join_success",
  ROOM_JOIN_FAILED: "room_join_failed",
  ROOM_ENTERED: "room_entered",
  
  // Chat Events
  MESSAGE_SENT: "message_sent",
  CHAT_EXITED: "chat_exited",
  
  // PWA Events
  INSTALL_BANNER_SHOWN: "install_banner_shown",
  INSTALL_BANNER_DISMISSED: "install_banner_dismissed",
  INSTALL_PROMPT_TRIGGERED: "install_prompt_triggered",
  APP_INSTALLED: "app_installed",
  
  // Location Events
  LOCATION_REQUESTED: "location_requested",
  LOCATION_GRANTED: "location_granted",
  LOCATION_DENIED: "location_denied",
  LOCATION_CHECK_FAILED: "location_check_failed",
  
  // Language
  LANGUAGE_CHANGED: "language_changed",
} as const;

type AnalyticsEventName = typeof AnalyticsEvents[keyof typeof AnalyticsEvents];

interface AnalyticsParams {
  [key: string]: string | number | boolean | undefined;
}

/**
 * Hook for logging Firebase Analytics events
 */
export function useAnalytics() {
  const log = useCallback((eventName: AnalyticsEventName, params?: AnalyticsParams) => {
    const analytics = getAnalyticsInstance();
    
    if (!analytics) {
      console.log(`📊 [Analytics - not initialized] ${eventName}`, params || {});
      return;
    }
    
    // Filter out undefined values
    const cleanParams = params 
      ? Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined))
      : undefined;
    
    // Cast to string to avoid strict typing issues with Firebase Analytics
    logEvent(analytics, eventName as string, cleanParams);
    console.log(`📊 [Analytics] ${eventName}`, cleanParams || {});
  }, []);
  
  // Convenience methods
  const logPageView = useCallback((pagePath: string, pageTitle?: string) => {
    log(AnalyticsEvents.PAGE_VIEW, { page_path: pagePath, page_title: pageTitle });
  }, [log]);
  
  const logAnonymousSignIn = useCallback((success: boolean, attemptNumber?: number) => {
    log(AnalyticsEvents.ANONYMOUS_SIGNIN, { success, attempt_number: attemptNumber });
  }, [log]);
  
  const logQrScanStarted = useCallback((cameraSupported: boolean) => {
    log(AnalyticsEvents.QR_SCAN_STARTED, { camera_supported: cameraSupported });
  }, [log]);
  
  const logQrCodeDetected = useCallback((code: string) => {
    log(AnalyticsEvents.QR_CODE_DETECTED, { code });
  }, [log]);
  
  const logManualCodeEntered = useCallback((code: string) => {
    log(AnalyticsEvents.MANUAL_CODE_ENTERED, { code });
  }, [log]);
  
  const logRoomJoinStarted = useCallback((code: string, method: "qr" | "manual" | "url") => {
    log(AnalyticsEvents.ROOM_JOIN_STARTED, { code, method });
  }, [log]);
  
  const logRoomJoinSuccess = useCallback((code: string, isNewRoom?: boolean) => {
    log(AnalyticsEvents.ROOM_JOIN_SUCCESS, { code, is_new_room: isNewRoom });
  }, [log]);
  
  const logRoomJoinFailed = useCallback((code: string, errorReason: string) => {
    log(AnalyticsEvents.ROOM_JOIN_FAILED, { code, error_reason: errorReason });
  }, [log]);
  
  const logRoomEntered = useCallback((code: string, from: "home" | "url") => {
    log(AnalyticsEvents.ROOM_ENTERED, { code, from });
  }, [log]);
  
  const logMessageSent = useCallback((roomCode: string, messageLength: number) => {
    log(AnalyticsEvents.MESSAGE_SENT, { room_code: roomCode, message_length: messageLength });
  }, [log]);
  
  const logChatExited = useCallback((roomCode: string, durationSeconds: number, messagesSent: number) => {
    log(AnalyticsEvents.CHAT_EXITED, { 
      room_code: roomCode, 
      duration_seconds: durationSeconds, 
      messages_sent: messagesSent 
    });
  }, [log]);
  
  const logInstallBannerShown = useCallback((platform: "ios" | "android" | "unknown") => {
    log(AnalyticsEvents.INSTALL_BANNER_SHOWN, { platform });
  }, [log]);
  
  const logInstallBannerDismissed = useCallback(() => {
    log(AnalyticsEvents.INSTALL_BANNER_DISMISSED);
  }, [log]);
  
  const logInstallPromptTriggered = useCallback(() => {
    log(AnalyticsEvents.INSTALL_PROMPT_TRIGGERED);
  }, [log]);
  
  const logAppInstalled = useCallback((platform: string) => {
    log(AnalyticsEvents.APP_INSTALLED, { platform });
  }, [log]);
  
  const logLocationRequested = useCallback(() => {
    log(AnalyticsEvents.LOCATION_REQUESTED);
  }, [log]);
  
  const logLocationGranted = useCallback((accuracyMeters?: number) => {
    log(AnalyticsEvents.LOCATION_GRANTED, { accuracy_meters: accuracyMeters });
  }, [log]);
  
  const logLocationDenied = useCallback(() => {
    log(AnalyticsEvents.LOCATION_DENIED);
  }, [log]);
  
  const logLocationCheckFailed = useCallback((roomCode: string) => {
    log(AnalyticsEvents.LOCATION_CHECK_FAILED, { room_code: roomCode });
  }, [log]);
  
  const logLanguageChanged = useCallback((fromLang: string, toLang: string) => {
    log(AnalyticsEvents.LANGUAGE_CHANGED, { from_lang: fromLang, to_lang: toLang });
  }, [log]);
  
  // Set user ID for retention tracking
  const setUserId = useCallback((userId: string | null) => {
    setAnalyticsUserId(userId);
  }, []);
  
  return {
    log,
    setUserId,
    logPageView,
    logAnonymousSignIn,
    logQrScanStarted,
    logQrCodeDetected,
    logManualCodeEntered,
    logRoomJoinStarted,
    logRoomJoinSuccess,
    logRoomJoinFailed,
    logRoomEntered,
    logMessageSent,
    logChatExited,
    logInstallBannerShown,
    logInstallBannerDismissed,
    logInstallPromptTriggered,
    logAppInstalled,
    logLocationRequested,
    logLocationGranted,
    logLocationDenied,
    logLocationCheckFailed,
    logLanguageChanged,
  };
}

